import React, { useEffect, useMemo, useState } from "react";
import AdminNav from "./AdminNav";
import SimpleCard from "@gull/components/cards/SimpleCard";
import ReactEcharts from "echarts-for-react";
import ReactPaginate from "react-paginate";
import FileSaver from "file-saver";

const PAGE_SIZE = 20;

const STATUS_LABELS = {
  approved: "Short List",
  final_stage: "Finalist",
};

const statusLabel = (status) => STATUS_LABELS[status] || status;

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const cleaned = String(value).replace(/,/g, "").trim();
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : 0;
};

const formatCurrency = (value) => {
  const number = toNumber(value);
  return `PKR ${number.toLocaleString("en-PK")}`;
};

const FundsRequestReport = () => {
  const token = localStorage.getItem("token");

  const [campaignName, setCampaignName] = useState("");
  const [records, setRecords] = useState([]);
  const [allocatedTotal, setAllocatedTotal] = useState(0);
  const [allocatedByApp, setAllocatedByApp] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      setError("");

      try {
        const campaignResponse = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/campaigns?sort=createdAt:desc&pagination[limit]=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!campaignResponse.ok) throw new Error("Failed to fetch campaign");

        const campaignData = await campaignResponse.json();
        const campaign = campaignData.data?.[0];

        if (!campaign) {
          setCampaignName("");
          setRecords([]);
          setLoading(false);
          return;
        }

        setCampaignName(campaign.name || "");
        localStorage.setItem("campaigin_id", String(campaign.id));

        const query = new URLSearchParams({
          "filters[application][campaign][id][$eq]": String(campaign.id),
          populate: "application",
          "pagination[pageSize]": "100",
          "pagination[page]": "1",
          sort: "createdAt:desc",
        });

        const allRecords = [];
        let page = 1;
        let pageCount = 1;
        do {
          query.set("pagination[page]", String(page));
          const response = await fetch(
            `${process.env.REACT_APP_BASE_URL_strapi}/finance-details?${query.toString()}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!response.ok) throw new Error("Failed to fetch finance details");

          const data = await response.json();
          if (!Array.isArray(data.data) || !Number.isInteger(data.meta?.pagination?.pageCount)) {
            throw new Error("Unexpected finance details response");
          }

          allRecords.push(...data.data);
          pageCount = data.meta.pagination.pageCount;
          page += 1;
        } while (page <= pageCount);

        // Allocated funds come from the finance-amount collection (field: amount)
        const amountQuery = new URLSearchParams({
          "filters[application][campaign][id][$eq]": String(campaign.id),
          populate: "application",
          "pagination[pageSize]": "100",
          "pagination[page]": "1",
        });
        const allocatedMap = new Map();
        let allocated = 0;
        let amountPage = 1;
        let amountPageCount = 1;
        do {
          amountQuery.set("pagination[page]", String(amountPage));
          const amountResponse = await fetch(
            `${process.env.REACT_APP_BASE_URL_strapi}/finance-amounts?${amountQuery.toString()}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!amountResponse.ok) throw new Error("Failed to fetch finance amounts");

          const amountData = await amountResponse.json();
          if (!Array.isArray(amountData.data) || !Number.isInteger(amountData.meta?.pagination?.pageCount)) {
            throw new Error("Unexpected finance amounts response");
          }

          amountData.data.forEach((item) => {
            const source = item.attributes || item;
            const appRaw = source.application || {};
            const app = appRaw.attributes || appRaw;
            if (app.id !== undefined) {
              allocatedMap.set(app.id, toNumber(source.amount));
            }
            allocated += toNumber(source.amount);
          });
          amountPageCount = amountData.meta.pagination.pageCount;
          amountPage += 1;
        } while (amountPage <= amountPageCount);

        setAllocatedTotal(allocated);
        setAllocatedByApp(allocatedMap);

        setRecords(
          allRecords.map((item) => {
            const source = item.attributes || item;
            const applicationRaw = source.application || {};
            const application = applicationRaw.attributes || applicationRaw;

            return {
              id: item.id,
              application_id: application.id,
              name: application.name || "N/A",
              email: application.email || "N/A",
              application_status: application.application_status || "N/A",
              funds_request: source.funds_request,
              funds_request_against: source.funds_request_against || "N/A",
              chalan: source.chalan,
            };
          })
        );
      } catch (err) {
        console.error("Error loading funds request report:", err);
        setError("Unable to load the report. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [token]);

  const summary = useMemo(() => {
    const totalRequested = records.reduce((sum, item) => sum + toNumber(item.funds_request), 0);
    // Incomplete applications have not completed the process yet, so their challan is excluded
    const totalChalan = records
      .filter((item) => item.application_status !== "incomplete")
      .reduce((sum, item) => sum + toNumber(item.chalan), 0);

    return {
      totalRequests: records.length,
      totalRequested,
      totalChalan,
      averageRequested: records.length ? totalRequested / records.length : 0,
    };
  }, [records]);

  const categoryBreakdown = useMemo(() => {
    const grouped = new Map();

    records.forEach((item) => {
      const key = item.funds_request_against || "Not Specified";
      const entry = grouped.get(key) || { name: key, count: 0, requested: 0, chalan: 0 };
      entry.count += 1;
      entry.requested += toNumber(item.funds_request);
      entry.chalan += toNumber(item.chalan);
      grouped.set(key, entry);
    });

    return Array.from(grouped.values());
  }, [records]);

  const statuses = useMemo(() => {
    const derived = records.map((item) => item.application_status).filter(Boolean);
    return Array.from(new Set([...derived, "approved", "final_stage"]));
  }, [records]);

  const filteredRecords = useMemo(() => {
    const term = searchText.trim().toLowerCase();

    return records.filter((item) => {
      const matchesStatus = !statusFilter || item.application_status === statusFilter;
      const matchesSearch =
        !term ||
        (item.name || "").toLowerCase().includes(term) ||
        (item.email || "").toLowerCase().includes(term);
      return matchesStatus && matchesSearch;
    });
  }, [records, statusFilter, searchText]);

  useEffect(() => {
    setCurrentPage(0);
  }, [statusFilter, searchText]);

  const pageCount = Math.ceil(filteredRecords.length / PAGE_SIZE);
  const pageRecords = filteredRecords.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE
  );

  const chartOption = useMemo(
    () => ({
      color: ["#0f766e", "#f59e0b"],
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: { data: ["Requested Amount", "Challan Amount"] },
      grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
      xAxis: {
        type: "category",
        data: categoryBreakdown.map((item) => item.name),
        axisLabel: { interval: 0, rotate: 30 },
      },
      yAxis: { type: "value" },
      series: [
        {
          name: "Requested Amount",
          type: "bar",
          data: categoryBreakdown.map((item) => item.requested),
        },
        {
          name: "Challan Amount",
          type: "bar",
          data: categoryBreakdown.map((item) => item.chalan),
        },
      ],
    }),
    [categoryBreakdown]
  );

  const exportCsv = () => {
    const headers = [
      "Applicant Name",
      "Email",
      "Funds Request",
      "Funds Request Against",
      "Challan",
      "Allocated Funds",
      "Application Status",
    ];
    const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const rows = filteredRecords.map((item) => [
      item.name,
      item.email,
      item.funds_request,
      item.funds_request_against,
      item.chalan,
      getAllocated(item),
      item.application_status,
    ]);
    const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=UTF-8" });
    FileSaver.saveAs(blob, "funds-request-report.csv");
  };

  const getAllocated = (item) => {
    if (item.application_id === undefined || !allocatedByApp.has(item.application_id)) {
      return "N/A";
    }
    return formatCurrency(allocatedByApp.get(item.application_id));
  };

  const cards = [
    { label: "Total Funds Requests", value: summary.totalRequests },
    { label: "Total Requested Amount", value: formatCurrency(summary.totalRequested) },
    { label: "Total Challan Amount", value: formatCurrency(summary.totalChalan) },
    { label: "Average Requested Amount", value: formatCurrency(summary.averageRequested) },
    { label: "Total Allocated Funds", value: formatCurrency(allocatedTotal) },
  ];

  return (
    <div className="app-admin-wrap layout-sidebar-large">
      <AdminNav />
      <div className="main-content-wrap d-flex flex-column sidenav-open">
        <h1>Funds Request Report</h1>
        <p className="text-muted mb-3">
          {campaignName ? `Campaign: ${campaignName}` : "No campaign selected"}
        </p>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="row">
          {cards.map((card) => (
            <div key={card.label} className="col-lg-3 col-md-6 col-sm-6">
              <div className="card card-icon-bg card-icon-bg-primary o-hidden mb-4">
                <div className="card-body">
                  <div className="content custom w-100 p-3">
                    <h1
                      className="text-primary text-right mb-2 text-capitalize"
                      style={{ fontSize: "1.5rem" }}
                    >
                      {card.value}
                    </h1>
                    <h5 className="text-muted text-left mt-2 mb-0 text-uppercase">
                      {card.label}
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="row">
          <div className="col-lg-6 col-sm-12">
            <SimpleCard title="Requested vs Challan by Category" className="mb-4">
              {categoryBreakdown.length > 0 ? (
                <ReactEcharts option={chartOption} style={{ height: "300px" }} />
              ) : (
                <p className="text-muted">No data available.</p>
              )}
            </SimpleCard>
          </div>
          <div className="col-lg-6 col-sm-12">
            <SimpleCard title="Category Summary" className="mb-4">
              {categoryBreakdown.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Requests</th>
                        <th>Requested</th>
                        <th>Challan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryBreakdown.map((item) => (
                        <tr key={item.name}>
                          <td>{item.name}</td>
                          <td>{item.count}</td>
                          <td>{formatCurrency(item.requested)}</td>
                          <td>{formatCurrency(item.chalan)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No data available.</p>
              )}
            </SimpleCard>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <div className="simple-card-title d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h3 className="text-capitalize mb-1">Funds Requests</h3>
                <small className="text-muted">{filteredRecords.length} records</small>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search name or email..."
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  style={{ minWidth: 220 }}
                />
                <select
                  className="form-control"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  style={{ minWidth: 160 }}
                >
                  <option value="">All Statuses</option>
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {statusLabel(status)}
                    </option>
                  ))}
                </select>
                <button className="btn btn-primary text-white" onClick={exportCsv}>
                  <span className="ul-btn__icon">
                    <i className="i-File-Excel"></i>
                  </span>
                  <span className="ul-btn__text">Export Data</span>
                </button>
              </div>
            </div>

            {pageRecords.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Applicant Name</th>
                      <th>Email</th>
                      <th>Funds Request</th>
                      <th>Funds Request Against</th>
                      <th>Challan</th>
                      <th>Allocated Funds</th>
                      <th>Application Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRecords.map((item, index) => (
                      <tr key={item.id}>
                        <td>{currentPage * PAGE_SIZE + index + 1}</td>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{formatCurrency(item.funds_request)}</td>
                        <td>{item.funds_request_against}</td>
                        <td>{formatCurrency(item.chalan)}</td>
                        <td>{getAllocated(item)}</td>
                        <td>{item.application_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted">
                {loading ? "Loading..." : "No funds requests found for this campaign."}
              </p>
            )}
          </div>
        </div>

        {pageCount > 1 && (
          <ReactPaginate
            previousLabel={"Previous"}
            nextLabel={"Next"}
            breakLabel={"..."}
            pageCount={pageCount}
            forcePage={currentPage}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={({ selected }) => setCurrentPage(selected)}
            containerClassName={"pagination"}
            activeClassName={"active"}
          />
        )}
      </div>

      {loading && (
        <div className="frr-overlay">
          <div className="frr-overlay__spinner" aria-label="Loading" role="status" />
        </div>
      )}
      <style>{`
        .frr-overlay {
          position: fixed; inset: 0; z-index: 100;
          background: rgba(255, 255, 255, .75);
          display: grid; place-items: center;
          backdrop-filter: blur(2px); -webkit-backdrop-filter: blur(2px);
        }
        .frr-overlay__spinner {
          width: 48px; height: 48px; border-radius: 50%;
          border: 4px solid #dfe6e4; border-top-color: #0f766e;
          animation: frr-spin .7s linear infinite;
        }
        @keyframes frr-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default FundsRequestReport;
